import React, { useState } from 'react';
import { SearchIcon, PlusIcon, CalendarIcon, CheckCircleIcon, ClockIcon, XIcon, AlertTriangleIcon, BellIcon, CheckIcon } from 'lucide-react';
export function ContractMilestones({
  locale
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [formData, setFormData] = useState({
    contractId: '',
    title: '',
    description: '',
    dueDate: '',
    completionDate: '',
    status: 'Pending',
    reminderDays: '7',
    notifyUsers: []
  });
  // Sample milestones data
  const milestones = [{
    id: 'MLS-2023-001',
    contractId: 'CTR-2023-002',
    contractName: 'IT Support Services',
    vendor: 'TechPro Solutions',
    title: 'Initial Setup Complete',
    description: 'Complete initial system setup and configuration',
    dueDate: '2023-03-15',
    completionDate: '2023-03-12',
    status: 'Completed',
    reminderDays: 7,
    notifyUsers: ['user1@example.com', 'user2@example.com']
  }, {
    id: 'MLS-2023-002',
    contractId: 'CTR-2023-002',
    contractName: 'IT Support Services',
    vendor: 'TechPro Solutions',
    title: 'First Quarterly Review',
    description: 'Conduct first quarterly service review meeting',
    dueDate: '2023-05-01',
    completionDate: '2023-05-03',
    status: 'Completed',
    reminderDays: 7,
    notifyUsers: ['user1@example.com', 'user3@example.com']
  }, {
    id: 'MLS-2023-003',
    contractId: 'CTR-2023-002',
    contractName: 'IT Support Services',
    vendor: 'TechPro Solutions',
    title: 'Second Quarterly Review',
    description: 'Conduct second quarterly service review meeting',
    dueDate: '2023-08-01',
    completionDate: '',
    status: 'Upcoming',
    reminderDays: 14,
    notifyUsers: ['user1@example.com', 'user3@example.com']
  }, {
    id: 'MLS-2023-004',
    contractId: 'CTR-2023-001',
    contractName: 'Office Lease Agreement',
    vendor: 'Skyline Properties Inc.',
    title: 'Annual Inspection',
    description: 'Complete annual property inspection',
    dueDate: '2023-12-15',
    completionDate: '',
    status: 'Upcoming',
    reminderDays: 30,
    notifyUsers: ['user4@example.com']
  }, {
    id: 'MLS-2023-005',
    contractId: 'CTR-2023-006',
    contractName: 'Consulting Services',
    vendor: 'Business Consultants Ltd.',
    title: 'Phase 1 Deliverable',
    description: 'Submit initial assessment report',
    dueDate: '2023-05-30',
    completionDate: '',
    status: 'Overdue',
    reminderDays: 7,
    notifyUsers: ['user1@example.com', 'user5@example.com']
  }, {
    id: 'MLS-2023-006',
    contractId: 'CTR-2023-010',
    contractName: 'Cloud Infrastructure',
    vendor: 'AWS',
    title: 'Migration Complete',
    description: 'Complete migration to new cloud infrastructure',
    dueDate: '2023-09-30',
    completionDate: '',
    status: 'At Risk',
    reminderDays: 14,
    notifyUsers: ['user6@example.com', 'user7@example.com']
  }];
  // Filter milestones based on search term
  const filteredMilestones = milestones.filter(milestone => milestone.id.toLowerCase().includes(searchTerm.toLowerCase()) || milestone.contractId.toLowerCase().includes(searchTerm.toLowerCase()) || milestone.contractName.toLowerCase().includes(searchTerm.toLowerCase()) || milestone.vendor.toLowerCase().includes(searchTerm.toLowerCase()) || milestone.title.toLowerCase().includes(searchTerm.toLowerCase()) || milestone.description.toLowerCase().includes(searchTerm.toLowerCase()));
  // Get status badge color
  const getStatusColor = status => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-800';
      case 'Upcoming':
        return 'bg-blue-100 text-blue-800';
      case 'Overdue':
        return 'bg-red-100 text-red-800';
      case 'At Risk':
        return 'bg-yellow-100 text-yellow-800';
      case 'Pending':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Get status icon
  const getStatusIcon = status => {
    switch (status) {
      case 'Completed':
        return CheckCircleIcon;
      case 'Upcoming':
        return ClockIcon;
      case 'Overdue':
        return XIcon;
      case 'At Risk':
        return AlertTriangleIcon;
      case 'Pending':
        return ClockIcon;
      default:
        return ClockIcon;
    }
  };
  const handleChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  const handleSubmit = e => {
    e.preventDefault();
    // In a real app, this would save the milestone to the database
    console.log('Saving milestone:', formData);
    // Show success message
    alert(`Milestone saved successfully!`);
    // Reset form and view
    setShowForm(false);
    setSelectedMilestone(null);
  };
  const handleAddNew = () => {
    setFormData({
      contractId: '',
      title: '',
      description: '',
      dueDate: '',
      completionDate: '',
      status: 'Pending',
      reminderDays: '7',
      notifyUsers: []
    });
    setSelectedMilestone(null);
    setShowForm(true);
  };
  const handleEdit = milestone => {
    setFormData({
      contractId: milestone.contractId,
      title: milestone.title,
      description: milestone.description,
      dueDate: milestone.dueDate,
      completionDate: milestone.completionDate,
      status: milestone.status,
      reminderDays: milestone.reminderDays.toString(),
      notifyUsers: milestone.notifyUsers
    });
    setSelectedMilestone(milestone);
    setShowForm(true);
  };
  const handleMarkComplete = id => {
    // In a real app, this would update the milestone status in the database
    console.log('Marking milestone complete:', id);
    alert(`Milestone ${id} marked as complete`);
  };
  return <div data-id="element-2607">
      <div className="mb-8 flex justify-between items-center" data-id="element-2608">
        <div data-id="element-2609">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-2610">
            Contract Milestones
          </h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-2611">
            Track important dates and deliverables for your contracts
          </p>
        </div>
        <button onClick={handleAddNew} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none" data-id="element-2612">
          <PlusIcon className="h-4 w-4 mr-2" data-id="element-2613" />
          Add Milestone
        </button>
      </div>
      {showForm ? <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-2614">
          <form onSubmit={handleSubmit} className="p-6" data-id="element-2615">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-2616">
              {/* Contract Selection */}
              <div className="sm:col-span-3" data-id="element-2617">
                <label htmlFor="contractId" className="block text-sm font-medium text-gray-700" data-id="element-2618">
                  Contract
                </label>
                <div className="mt-1" data-id="element-2619">
                  <select id="contractId" name="contractId" value={formData.contractId} onChange={handleChange} required className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2620">
                    <option value="" data-id="element-2621">Select a contract</option>
                    <option value="CTR-2023-001" data-id="element-2622">
                      CTR-2023-001 - Office Lease Agreement
                    </option>
                    <option value="CTR-2023-002" data-id="element-2623">
                      CTR-2023-002 - IT Support Services
                    </option>
                    <option value="CTR-2023-006" data-id="element-2624">
                      CTR-2023-006 - Consulting Services
                    </option>
                    <option value="CTR-2023-008" data-id="element-2625">
                      CTR-2023-008 - Security Services
                    </option>
                    <option value="CTR-2023-010" data-id="element-2626">
                      CTR-2023-010 - Cloud Infrastructure
                    </option>
                  </select>
                </div>
              </div>
              {/* Milestone Title */}
              <div className="sm:col-span-3" data-id="element-2627">
                <label htmlFor="title" className="block text-sm font-medium text-gray-700" data-id="element-2628">
                  Milestone Title
                </label>
                <div className="mt-1" data-id="element-2629">
                  <input type="text" name="title" id="title" value={formData.title} onChange={handleChange} required className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2630" />
                </div>
              </div>
              {/* Description */}
              <div className="sm:col-span-6" data-id="element-2631">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-2632">
                  Description
                </label>
                <div className="mt-1" data-id="element-2633">
                  <textarea id="description" name="description" rows={2} value={formData.description} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2634" />
                </div>
              </div>
              {/* Due Date */}
              <div className="sm:col-span-3" data-id="element-2635">
                <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700" data-id="element-2636">
                  Due Date
                </label>
                <div className="mt-1" data-id="element-2637">
                  <input type="date" name="dueDate" id="dueDate" value={formData.dueDate} onChange={handleChange} required className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2638" />
                </div>
              </div>
              {/* Completion Date */}
              <div className="sm:col-span-3" data-id="element-2639">
                <label htmlFor="completionDate" className="block text-sm font-medium text-gray-700" data-id="element-2640">
                  Completion Date
                </label>
                <div className="mt-1" data-id="element-2641">
                  <input type="date" name="completionDate" id="completionDate" value={formData.completionDate} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2642" />
                </div>
              </div>
              {/* Status */}
              <div className="sm:col-span-3" data-id="element-2643">
                <label htmlFor="status" className="block text-sm font-medium text-gray-700" data-id="element-2644">
                  Status
                </label>
                <div className="mt-1" data-id="element-2645">
                  <select id="status" name="status" value={formData.status} onChange={handleChange} required className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2646">
                    <option value="Pending" data-id="element-2647">Pending</option>
                    <option value="Upcoming" data-id="element-2648">Upcoming</option>
                    <option value="At Risk" data-id="element-2649">At Risk</option>
                    <option value="Overdue" data-id="element-2650">Overdue</option>
                    <option value="Completed" data-id="element-2651">Completed</option>
                  </select>
                </div>
              </div>
              {/* Reminder Days */}
              <div className="sm:col-span-3" data-id="element-2652">
                <label htmlFor="reminderDays" className="block text-sm font-medium text-gray-700" data-id="element-2653">
                  Send Reminder (days before due date)
                </label>
                <div className="mt-1" data-id="element-2654">
                  <select id="reminderDays" name="reminderDays" value={formData.reminderDays} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2655">
                    <option value="1" data-id="element-2656">1 day</option>
                    <option value="3" data-id="element-2657">3 days</option>
                    <option value="7" data-id="element-2658">1 week</option>
                    <option value="14" data-id="element-2659">2 weeks</option>
                    <option value="30" data-id="element-2660">1 month</option>
                    <option value="60" data-id="element-2661">2 months</option>
                    <option value="90" data-id="element-2662">3 months</option>
                  </select>
                </div>
              </div>
              {/* Notify Users */}
              <div className="sm:col-span-6" data-id="element-2663">
                <label htmlFor="notifyUsers" className="block text-sm font-medium text-gray-700" data-id="element-2664">
                  Notify Users (comma separated emails)
                </label>
                <div className="mt-1" data-id="element-2665">
                  <input type="text" name="notifyUsers" id="notifyUsers" value={formData.notifyUsers.join(', ')} onChange={e => {
                const emails = e.target.value.split(',').map(email => email.trim()).filter(email => email);
                setFormData({
                  ...formData,
                  notifyUsers: emails
                });
              }} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="user1@example.com, user2@example.com" data-id="element-2666" />
                </div>
              </div>
            </div>
            <div className="pt-5 mt-6 border-t border-gray-200" data-id="element-2667">
              <div className="flex justify-end" data-id="element-2668">
                <button type="button" onClick={() => setShowForm(false)} className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none" data-id="element-2669">
                  Cancel
                </button>
                <button type="submit" className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none" data-id="element-2670">
                  {selectedMilestone ? 'Update Milestone' : 'Create Milestone'}
                </button>
              </div>
            </div>
          </form>
        </div> : <>
          {/* Search */}
          <div className="mb-6" data-id="element-2671">
            <div className="max-w-md" data-id="element-2672">
              <div className="relative rounded-md shadow-sm" data-id="element-2673">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2674">
                  <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-2675" />
                </div>
                <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search milestones..." data-id="element-2676" />
              </div>
            </div>
          </div>
          {/* Milestones Stats */}
          <div className="mb-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-2677">
            <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-2678">
              <div className="px-4 py-5 sm:p-6" data-id="element-2679">
                <div className="flex items-center" data-id="element-2680">
                  <div className="flex-shrink-0 bg-green-100 rounded-md p-3" data-id="element-2681">
                    <CheckCircleIcon className="h-6 w-6 text-green-600" data-id="element-2682" />
                  </div>
                  <div className="ml-5 w-0 flex-1" data-id="element-2683">
                    <dl data-id="element-2684">
                      <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-2685">
                        Completed
                      </dt>
                      <dd data-id="element-2686">
                        <div className="text-lg font-medium text-gray-900" data-id="element-2687">
                          {milestones.filter(m => m.status === 'Completed').length}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-2688">
              <div className="px-4 py-5 sm:p-6" data-id="element-2689">
                <div className="flex items-center" data-id="element-2690">
                  <div className="flex-shrink-0 bg-blue-100 rounded-md p-3" data-id="element-2691">
                    <ClockIcon className="h-6 w-6 text-blue-600" data-id="element-2692" />
                  </div>
                  <div className="ml-5 w-0 flex-1" data-id="element-2693">
                    <dl data-id="element-2694">
                      <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-2695">
                        Upcoming
                      </dt>
                      <dd data-id="element-2696">
                        <div className="text-lg font-medium text-gray-900" data-id="element-2697">
                          {milestones.filter(m => m.status === 'Upcoming').length}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-2698">
              <div className="px-4 py-5 sm:p-6" data-id="element-2699">
                <div className="flex items-center" data-id="element-2700">
                  <div className="flex-shrink-0 bg-yellow-100 rounded-md p-3" data-id="element-2701">
                    <AlertTriangleIcon className="h-6 w-6 text-yellow-600" data-id="element-2702" />
                  </div>
                  <div className="ml-5 w-0 flex-1" data-id="element-2703">
                    <dl data-id="element-2704">
                      <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-2705">
                        At Risk
                      </dt>
                      <dd data-id="element-2706">
                        <div className="text-lg font-medium text-gray-900" data-id="element-2707">
                          {milestones.filter(m => m.status === 'At Risk').length}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-2708">
              <div className="px-4 py-5 sm:p-6" data-id="element-2709">
                <div className="flex items-center" data-id="element-2710">
                  <div className="flex-shrink-0 bg-red-100 rounded-md p-3" data-id="element-2711">
                    <XIcon className="h-6 w-6 text-red-600" data-id="element-2712" />
                  </div>
                  <div className="ml-5 w-0 flex-1" data-id="element-2713">
                    <dl data-id="element-2714">
                      <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-2715">
                        Overdue
                      </dt>
                      <dd data-id="element-2716">
                        <div className="text-lg font-medium text-gray-900" data-id="element-2717">
                          {milestones.filter(m => m.status === 'Overdue').length}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Milestones Table */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-2718">
            <div className="overflow-x-auto" data-id="element-2719">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-2720">
                <thead className="bg-gray-50" data-id="element-2721">
                  <tr data-id="element-2722">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2723">
                      Milestone
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2724">
                      Contract
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2725">
                      Description
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2726">
                      Dates
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2727">
                      Reminder
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2728">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2729">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-2730">
                  {filteredMilestones.length === 0 ? <tr data-id="element-2731">
                      <td colSpan={7} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-2732">
                        No milestones found matching your criteria.
                      </td>
                    </tr> : filteredMilestones.map(milestone => {
                const StatusIcon = getStatusIcon(milestone.status);
                return <tr key={milestone.id} className="hover:bg-gray-50" data-id="element-2733">
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-2734">
                            <div className="flex items-center" data-id="element-2735">
                              <div data-id="element-2736">
                                <div className="text-sm font-medium text-indigo-600" data-id="element-2737">
                                  {milestone.id}
                                </div>
                                <div className="text-sm text-gray-500" data-id="element-2738">
                                  {milestone.title}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-2739">
                            <div className="text-sm text-gray-900" data-id="element-2740">
                              {milestone.contractId}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-2741">
                              {milestone.contractName}
                            </div>
                            <div className="text-xs text-gray-500" data-id="element-2742">
                              {milestone.vendor}
                            </div>
                          </td>
                          <td className="px-6 py-4" data-id="element-2743">
                            <div className="text-sm text-gray-900 max-w-xs truncate" data-id="element-2744">
                              {milestone.description}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2745">
                            <div className="flex items-center" data-id="element-2746">
                              <CalendarIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-2747" />
                              <span data-id="element-2748">Due: {milestone.dueDate}</span>
                            </div>
                            {milestone.completionDate && <div className="flex items-center mt-1" data-id="element-2749">
                                <CalendarIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-2750" />
                                <span data-id="element-2751">
                                  Completed: {milestone.completionDate}
                                </span>
                              </div>}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2752">
                            <div className="flex items-center" data-id="element-2753">
                              <BellIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-2754" />
                              <span data-id="element-2755">{milestone.reminderDays} days before</span>
                            </div>
                            <div className="text-xs text-gray-500 mt-1" data-id="element-2756">
                              {milestone.notifyUsers.length} users notified
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-2757">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(milestone.status)}`} data-id="element-2758">
                              <StatusIcon className="h-3 w-3 mr-1" data-id="element-2759" />
                              {milestone.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2760">
                            <div className="flex justify-end space-x-2" data-id="element-2761">
                              <button onClick={() => handleEdit(milestone)} className="text-indigo-600 hover:text-indigo-900" data-id="element-2762">
                                Edit
                              </button>
                              {milestone.status !== 'Completed' && <button onClick={() => handleMarkComplete(milestone.id)} className="text-green-600 hover:text-green-900" title="Mark as Complete" data-id="element-2763">
                                  <CheckIcon className="h-4 w-4" data-id="element-2764" />
                                </button>}
                            </div>
                          </td>
                        </tr>;
              })}
                </tbody>
              </table>
            </div>
          </div>
        </>}
    </div>;
}